import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
} from 'prosci-ui';
import { FolderIcon, FolderOpenIcon, FileMdIcon } from '@/lib/icons';
import { getFileIcon } from './file-icons';
import type { SkillFormData } from './skill-form';

type Props = {
  form: SkillFormData;
};

export function SkillFolderPreview({ form }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Skill Folder</CardTitle>
        <CardDescription>How this skill will be structured when uploaded</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Folder tree */}
        <div className="text-xs font-mono bg-muted p-3 rounded-md space-y-1">
          <div className="flex items-center gap-1.5">
            <FolderOpenIcon className="h-4 w-4 text-amber-600" />
            <span className="font-semibold">{form.name || 'skill-name'}/</span>
          </div>
          <div className="ml-5 flex items-center gap-1.5">
            <FileMdIcon className="h-3 w-3 text-blue-600" />
            <span>SKILL.md</span>
            <Badge variant="outline" className="text-[10px] py-0 px-1 ml-1">required</Badge>
          </div>
          {Object.keys(form.scripts).length > 0 && (
            <>
              <div className="ml-5 flex items-center gap-1.5">
                <FolderIcon className="h-3 w-3 text-amber-600" />
                <span>scripts/</span>
              </div>
              {Object.keys(form.scripts).map((filename) => {
                const Icon = getFileIcon(filename);
                return (
                  <div key={filename} className="ml-10 flex items-center gap-1.5">
                    <Icon className="h-3 w-3 text-muted-foreground" />
                    <span>{filename}</span>
                  </div>
                );
              })}
            </>
          )}
          {Object.keys(form.references).length > 0 && (
            <>
              <div className="ml-5 flex items-center gap-1.5">
                <FolderIcon className="h-3 w-3 text-amber-600" />
                <span>references/</span>
              </div>
              {Object.keys(form.references).map((filename) => {
                const Icon = getFileIcon(filename);
                return (
                  <div key={filename} className="ml-10 flex items-center gap-1.5">
                    <Icon className="h-3 w-3 text-muted-foreground" />
                    <span>{filename}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* SKILL.md content preview */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">SKILL.md content:</p>
          <pre className="text-xs font-mono bg-muted p-3 rounded-md overflow-auto max-h-[200px] whitespace-pre-wrap">
            {`---\nname: ${form.name}\ndescription: ${form.description}\n---\n\n${form.skill_md_content}`}
          </pre>
        </div>

        {/* File count summary */}
        <p className="text-xs text-muted-foreground">
          {Object.keys(form.scripts).length} script(s), {Object.keys(form.references).length} reference(s)
        </p>
      </CardContent>
    </Card>
  );
}
